// ./src/config/placeTypeOptions.ts
// Place type options configuration for saved places
// These options are used in PlaceTypeSelector and PlaceTypeBadge components

import { PLACE_TYPE, PlaceTypeValue } from "@/utils/api";

// ============================================
// Types
// ============================================

export interface PlaceTypeOption {
  /** Value stored in database */
  value: PlaceTypeValue;
  /** Translation key for label — use with t('savedPlaces:placeTypes.home') */
  labelKey: string;
  /** Tabler icon name */
  icon: string;
  /** Mantine color */
  color: string;
}

// ============================================
// Place Type Options
// ============================================

export const PLACE_TYPE_OPTIONS: PlaceTypeOption[] = [
  { value: PLACE_TYPE.HOME, labelKey: "placeTypes.home", icon: "Home", color: "blue" },
  { value: PLACE_TYPE.OFFICE, labelKey: "placeTypes.office", icon: "Building", color: "cyan" },
  { value: PLACE_TYPE.HQ, labelKey: "placeTypes.hq", icon: "BuildingSkyscraper", color: "indigo" },
  { value: PLACE_TYPE.BRANCH, labelKey: "placeTypes.branch", icon: "BuildingCommunity", color: "teal" },
  { value: PLACE_TYPE.CLIENT, labelKey: "placeTypes.client", icon: "Users", color: "violet" },
  { value: PLACE_TYPE.GAS_STATION, labelKey: "placeTypes.gas_station", icon: "GasStation", color: "orange" },
  { value: PLACE_TYPE.MECHANIC, labelKey: "placeTypes.mechanic", icon: "Tool", color: "red" },
  { value: PLACE_TYPE.STORE, labelKey: "placeTypes.store", icon: "ShoppingCart", color: "green" },
  { value: PLACE_TYPE.POS, labelKey: "placeTypes.pos", icon: "Cash", color: "lime" },
  { value: PLACE_TYPE.WAREHOUSE, labelKey: "placeTypes.warehouse", icon: "BuildingWarehouse", color: "yellow" },
  { value: PLACE_TYPE.WHOLESALE, labelKey: "placeTypes.wholesale", icon: "Packages", color: "pink" },
  { value: PLACE_TYPE.OTHER, labelKey: "placeTypes.other", icon: "MapPin", color: "gray" },
];

// ============================================
// Lookup Maps
// ============================================

export const PLACE_TYPE_MAP: Record<string, PlaceTypeOption> = Object.fromEntries(
  PLACE_TYPE_OPTIONS.map((opt) => [opt.value, opt])
);

// ============================================
// Helper Functions
// ============================================

/** Get the full option for a place type. Returns undefined if not found. */
export const getPlaceTypeOption = (
  value: PlaceTypeValue | string | null | undefined
): PlaceTypeOption | undefined => {
  if (!value) return undefined;
  return PLACE_TYPE_MAP[value];
};

/** Get the icon name for a place type. Falls back to "MapPin". */
export const getPlaceTypeIcon = (
  value: PlaceTypeValue | string | null | undefined
): string => {
  return getPlaceTypeOption(value)?.icon ?? "MapPin";
};

/** Get the Mantine color for a place type. Falls back to "gray". */
export const getPlaceTypeColor = (
  value: PlaceTypeValue | string | null | undefined
): string => {
  return getPlaceTypeOption(value)?.color ?? "gray";
};